/**
 * MaturityBadge — 성숙도 등급 배지 (queue #643 · docs/biz-specs/common-showcase.md
 * §UAT-2026-07-07-R643 §R643-5/§R643-6)
 *
 * `deriveMaturity(node)`(git-독립 · module-versions.json 선언버전 + supersededBy 파생)로
 * GA/Beta/Deprecated 를 판정해 `StatusPill` 로 표시하고, 정책 설명(툴팁/aria-label) +
 * git recency 보조 라인(module-maturity.json · 있을 때만) 을 함께 렌더한다.
 * `ModuleDetailBody` 에서 `SupersedeNotice` 직후·version `<dl>` 직전에 삽입된다(InfoPanel·
 * 데모 패널 공유 본문이라 양쪽 자동 반영).
 */
import { useTranslation } from 'react-i18next';
import { StatusPill, type StatusTone } from '@ui-kit';
import type { ModuleNode, ModuleVersions, ModuleMaturity } from '../types/showcase';
import { deriveMaturity, SUPERSEDE_JUMP_DISABLED_CODES } from '../data/maturity';
import moduleVersionsJson from '../data/module-versions.json';
import moduleMaturityJson from '../data/module-maturity.json';

const MODULE_VERSIONS = moduleVersionsJson as unknown as ModuleVersions;
const MODULE_MATURITY = moduleMaturityJson as unknown as ModuleMaturity;

const TONE_BY_GRADE: Record<string, StatusTone> = {
  GA: 'success',
  BETA: 'info',
  DEPRECATED: 'warning',
};

interface Props {
  node: ModuleNode;
}

export function MaturityBadge({ node }: Props) {
  const { t } = useTranslation();
  const grade = deriveMaturity(node, MODULE_VERSIONS);
  const gradeKey = grade.toLowerCase() as 'ga' | 'beta' | 'deprecated';
  const badgeLabel = t(`showcase.maturity.badge.${gradeKey}`);
  const policyText = t(`showcase.maturity.policy.${gradeKey}`);

  const successor = node.supersededBy;
  const hasCodeSuccessor = typeof successor === 'string' && successor !== 'gateway-self';
  const jumpDisabled = typeof successor === 'string' && SUPERSEDE_JUMP_DISABLED_CODES.has(node.code);

  const maturityEntry = MODULE_MATURITY.modules?.[node.artifact];
  const hasRecency = !!maturityEntry?.lastChanged;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', marginBottom: 'var(--spacing-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        <span title={policyText} aria-label={`${badgeLabel} — ${policyText}`}>
          <StatusPill tone={TONE_BY_GRADE[grade]}>{badgeLabel}</StatusPill>
        </span>
        <button
          type="button"
          title={t('showcase.maturity.policy.title')}
          aria-label={t('showcase.maturity.policy.title')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--color-surface-border)',
            background: 'none',
            color: 'var(--color-text-muted)',
            fontSize: 10,
            fontWeight: 700,
            cursor: 'help',
            padding: 0,
          }}
        >
          ?
        </button>
      </div>

      {grade === 'DEPRECATED' && hasCodeSuccessor && (
        <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
          {jumpDisabled
            ? t('showcase.maturity.deprecatedSeeAbove')
            : t('showcase.maturity.deprecatedWithSuccessor', { code: successor })}
        </p>
      )}

      {hasRecency && (
        <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {t('showcase.maturity.lastChanged', { date: maturityEntry!.lastChanged })}
          {' · '}
          {t('showcase.maturity.commitCount', { count: maturityEntry!.commitCount })}
          {MODULE_MATURITY.shallow && <> {t('showcase.maturity.shallowNote')}</>}
        </p>
      )}
    </div>
  );
}
